import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Chip, CircularProgress, Snackbar } from 'ui/mui';
import { BrokenImageRounded, DoneAllRounded } from 'ui/icons';
import { supabase } from '../supabaseClient';
import { CardrushPageTabs } from './CardrushProposals';
import './CardrushTinder.css';

const VOTER_EMAIL = 'randolph@pokerand.net';
const SWIPE_THRESHOLD = 120;
const SWIPE_ANIMATION_MS = 280;

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function shuffleCards(items) {
  const nextItems = [...items];

  for (let index = nextItems.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [nextItems[index], nextItems[swapIndex]] = [nextItems[swapIndex], nextItems[index]];
  }

  return nextItems;
}

function collapseVotes(rows) {
  const latestByPid = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row?.pid) return;
    latestByPid.set(String(row.pid), row);
  });

  return Array.from(latestByPid.values());
}

function formatYen(value) {
  const amount = Number(value) || 0;
  return `¥${new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 }).format(amount)}`;
}

function formatGbp(value) {
  const amount = Number(value) || 0;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export default function CardrushTinder() {
  const dragStartRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cards, setCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [history, setHistory] = useState([]);
  const [voteCounts, setVoteCounts] = useState({ yes: 0, no: 0 });
  const [imageErrors, setImageErrors] = useState({});
  const [drag, setDrag] = useState({ active: false, x: 0, y: 0 });
  const [animatingDirection, setAnimatingDirection] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });
  const [hammerPrice, setHammerPrice] = useState(40);

  const currentCard = cards[currentIndex] || null;
  const visibleCards = cards.slice(currentIndex, currentIndex + 3);
  const totalCards = cards.length;
  const reviewedCount = Math.min(currentIndex, totalCards);
  const progressPercent = totalCards === 0 ? 100 : (reviewedCount / totalCards) * 100;
  const isComplete = !loading && currentIndex >= totalCards;
  const canVote = Boolean(currentCard) && !submitting;
  const canUndo = history.length > 0 && !submitting;

  const showToast = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      severity,
      message,
    });
  };

  useEffect(() => {
    let isMounted = true;

    const loadCards = async () => {
      setLoading(true);

      try {
        const [votesResponse, cardsResponse] = await Promise.all([
          supabase
            .from('card_votes')
            .select('id, pid, vote, voted_at')
            .eq('voter_email', VOTER_EMAIL)
            .order('voted_at', { ascending: true }),
          // Fetch all cards in batches to bypass 1000-row limit
          (async () => {
            const allCards = [];
            const batchSize = 1000;
            let offset = 0;
            while (true) {
              const { data, error } = await supabase
                .from('japan_cards')
                .select('pid, name, rarity, price_jpy, price_gbp, image_url, in_stock')
                .gt('price_jpy', 0)
                .range(offset, offset + batchSize - 1);
              if (error) return { data: allCards, error };
              allCards.push(...(data || []));
              if (!data || data.length < batchSize) break;
              offset += batchSize;
            }
            return { data: allCards, error: null };
          })(),
        ]);

        if (votesResponse.error) throw votesResponse.error;
        if (cardsResponse.error) throw cardsResponse.error;

        const uniqueVotes = collapseVotes(votesResponse.data);
        const votedPids = new Set(uniqueVotes.map((vote) => String(vote.pid)));
        const nextVoteCounts = uniqueVotes.reduce(
          (summary, vote) => {
            if (vote.vote === 'yes') {
              summary.yes += 1;
            } else if (vote.vote === 'no') {
              summary.no += 1;
            }

            return summary;
          },
          { yes: 0, no: 0 }
        );

        const nextCards = shuffleCards(
          (Array.isArray(cardsResponse.data) ? cardsResponse.data : []).filter(
            (card) => !votedPids.has(String(card.pid))
          )
        );

        if (!isMounted) return;

        setCards(nextCards);
        setCurrentIndex(0);
        setHistory([]);
        setVoteCounts(nextVoteCounts);
        setImageErrors({});
        setDrag({ active: false, x: 0, y: 0 });
        setAnimatingDirection(null);
      } catch (error) {
        if (!isMounted) return;

        setCards([]);
        setCurrentIndex(0);
        setHistory([]);
        setVoteCounts({ yes: 0, no: 0 });
        showToast(error.message || 'Failed to load Cardrush cards.', 'error');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadCards();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const tagName = event.target?.tagName;
      if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        if (canVote) {
          handleVote('no');
        }
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (canVote) {
          handleVote('yes');
        }
      } else if (event.key === 'z' || event.key === 'Z') {
        event.preventDefault();
        if (canUndo) {
          handleUndo();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  });

  async function handleVote(direction) {
    if (!currentCard || submitting) return;

    setSubmitting(true);
    setAnimatingDirection(direction);
    setDrag({ active: false, x: 0, y: 0 });
    dragStartRef.current = null;

    const animationDelay = delay(SWIPE_ANIMATION_MS);

    try {
      const { data, error } = await supabase
        .from('card_votes')
        .insert({
          pid: currentCard.pid,
          vote: direction,
          voter_email: VOTER_EMAIL,
        })
        .select('id, pid, vote, voted_at')
        .single();

      await animationDelay;

      if (error) throw error;

      setHistory((currentHistory) => [
        ...currentHistory,
        {
          card: currentCard,
          vote: data,
        },
      ]);
      setVoteCounts((currentCounts) => ({
        ...currentCounts,
        [direction]: currentCounts[direction] + 1,
      }));
      setCurrentIndex((value) => value + 1);
    } catch (error) {
      await animationDelay;
      showToast(error.message || `Failed to save ${direction} vote.`, 'error');
    } finally {
      setAnimatingDirection(null);
      setSubmitting(false);
    }
  }

  async function handleUndo() {
    if (!canUndo) return;

    const lastAction = history[history.length - 1];
    if (!lastAction?.vote?.id) return;

    setSubmitting(true);
    setAnimatingDirection(null);
    setDrag({ active: false, x: 0, y: 0 });
    dragStartRef.current = null;

    try {
      const { error } = await supabase
        .from('card_votes')
        .delete()
        .eq('id', lastAction.vote.id)
        .eq('voter_email', VOTER_EMAIL);

      if (error) throw error;

      setHistory((currentHistory) => currentHistory.slice(0, -1));
      setVoteCounts((currentCounts) => ({
        ...currentCounts,
        [lastAction.vote.vote]: Math.max(0, currentCounts[lastAction.vote.vote] - 1),
      }));
      setCurrentIndex((value) => Math.max(0, value - 1));
    } catch (error) {
      showToast(error.message || 'Failed to undo the last vote.', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  function handlePointerDown(event) {
    if (!currentCard || submitting) return;

    dragStartRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };

    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDrag({ active: true, x: 0, y: 0 });
  }

  function handlePointerMove(event) {
    if (!dragStartRef.current || dragStartRef.current.pointerId !== event.pointerId) return;

    const nextX = event.clientX - dragStartRef.current.startX;
    const nextY = event.clientY - dragStartRef.current.startY;

    setDrag({
      active: true,
      x: nextX,
      y: nextY,
    });
  }

  function handlePointerEnd(event) {
    if (!dragStartRef.current || dragStartRef.current.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - dragStartRef.current.startX;
    const deltaY = event.clientY - dragStartRef.current.startY;
    dragStartRef.current = null;

    if (Math.abs(deltaX) >= SWIPE_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY) * 0.85) {
      handleVote(deltaX > 0 ? 'yes' : 'no');
      return;
    }

    setDrag({ active: false, x: 0, y: 0 });
  }

  function handlePointerCancel(event) {
    if (!dragStartRef.current || dragStartRef.current.pointerId !== event.pointerId) return;
    dragStartRef.current = null;
    setDrag({ active: false, x: 0, y: 0 });
  }

  const yesIndicatorOpacity =
    animatingDirection === 'yes' ? 1 : drag.x > 0 ? Math.min(Math.abs(drag.x) / SWIPE_THRESHOLD, 1) : 0;
  const noIndicatorOpacity =
    animatingDirection === 'no' ? 1 : drag.x < 0 ? Math.min(Math.abs(drag.x) / SWIPE_THRESHOLD, 1) : 0;

  return (
    <div className="cr-tinder">
      <header className="cr-header cr-header--tinder">
        <div className="cr-header__text">
          <p className="rc-eyebrow">Buying</p>
          <h1 className="rc-page-title">
            Cardrush Tinder
          </h1>
          <p className="rc-page-description">
            Review in-stock Japan cards with quick yes/no voting.
          </p>
        </div>
        <div className="cr-header__actions">
          <CardrushPageTabs active="tinder" />
        </div>
      </header>

      <section className="cr-progress">
        <div className="cr-progress__track">
          <div
            className="cr-progress__bar"
            style={{ transform: `scaleX(${progressPercent / 100})` }}
          />
        </div>

        <div className="cr-progress__meta">
          <p className="cr-progress__count">
            {reviewedCount} of {totalCards} cards reviewed
          </p>
          <p className="cr-progress__votes">
            {voteCounts.yes} YES votes · {voteCounts.no} NO votes
          </p>
        </div>
      </section>

      <section className="cr-deck-area">
        {loading ? (
          <div className="cr-loading cr-loading--deck">
            <CircularProgress size={24} />
          </div>
        ) : isComplete ? (
          <div className="cr-done">
            <div className="cr-done__inner">
              <span className="rc-empty__icon"><DoneAllRounded size={20} /></span>
              <p className="cr-done__title">
                All done!
              </p>
              <p className="cr-done__text">
                {totalCards === 0
                  ? 'All available cards already have a vote.'
                  : 'You’ve reviewed every card in this queue.'}
              </p>
              <p className="cr-done__votes">
                {voteCounts.yes} YES votes · {voteCounts.no} NO votes
              </p>
              {history.length > 0 ? (
                <Button
                  type="button"
                  onClick={handleUndo}
                  disabled={!canUndo}
                  variant="outlined"
                  className="cr-done__undo"
                >
                  Undo last vote
                </Button>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="cr-deck">
            {visibleCards.map((card, index) => {
              const isTopCard = index === 0;
              const stackScale = isTopCard ? 1 : index === 1 ? 0.96 : 0.92;
              const stackOffset = isTopCard ? 0 : index === 1 ? 16 : 32;
              const imageFailed = imageErrors[String(card.pid)];

              let transform = `scale(${stackScale}) translateY(${stackOffset}px)`;
              let opacity = index === 0 ? 1 : index === 1 ? 0.72 : 0.48;

              if (isTopCard) {
                if (animatingDirection === 'yes') {
                  transform = 'translateX(150%) rotate(24deg)';
                  opacity = 0;
                } else if (animatingDirection === 'no') {
                  transform = 'translateX(-150%) rotate(-24deg)';
                  opacity = 0;
                } else {
                  transform = `translateX(${drag.x}px) translateY(${drag.y * 0.12}px) rotate(${drag.x / 18}deg)`;
                }
              }

              return (
                <div
                  key={card.pid}
                  onPointerDown={isTopCard ? handlePointerDown : undefined}
                  onPointerMove={isTopCard ? handlePointerMove : undefined}
                  onPointerUp={isTopCard ? handlePointerEnd : undefined}
                  onPointerCancel={isTopCard ? handlePointerCancel : undefined}
                  className="cr-swipe"
                  data-top={isTopCard ? 'true' : undefined}
                  data-lifted={isTopCard && (drag.active || animatingDirection) ? 'true' : undefined}
                  style={{
                    display: index > 2 ? 'none' : 'flex',
                    transform,
                    opacity,
                    transition: isTopCard
                      ? drag.active
                        ? 'none'
                        : `transform ${SWIPE_ANIMATION_MS}ms var(--rc-ease), opacity ${SWIPE_ANIMATION_MS}ms var(--rc-ease), box-shadow 300ms var(--rc-ease)`
                      : 'transform 250ms var(--rc-ease), opacity 250ms var(--rc-ease)',
                    cursor: isTopCard ? (drag.active ? 'grabbing' : 'grab') : 'default',
                    touchAction: isTopCard ? 'none' : 'auto',
                    zIndex: 10 - index,
                  }}
                >
                  <div className="cr-swipe__stage">
                    {!imageFailed && card.image_url ? (
                      <img
                        src={card.image_url}
                        alt={card.name || 'Card image'}
                        onError={() =>
                          setImageErrors((currentErrors) => ({
                            ...currentErrors,
                            [String(card.pid)]: true,
                          }))
                        }
                        className="cr-swipe__img"
                      />
                    ) : (
                      <div className="cr-missing">
                        <div className="cr-missing__inner">
                          <BrokenImageRounded size={32} />
                          <p>Image unavailable</p>
                          {card.cardrush_url && (
                            <a
                              href={card.cardrush_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              onPointerDown={e => e.stopPropagation()}
                              className="cr-missing__link"
                            >
                              View on Cardrush →
                            </a>
                          )}
                        </div>
                      </div>
                    )}

                    {isTopCard ? (
                      <>
                        <div className="cr-stamp cr-stamp--no" style={{ opacity: noIndicatorOpacity }}>
                          ✕
                        </div>
                        <div className="cr-stamp cr-stamp--yes" style={{ opacity: yesIndicatorOpacity }}>
                          ♥
                        </div>
                      </>
                    ) : null}
                  </div>

                  <div className="cr-swipe__info">
                    <div className="cr-swipe__top">
                      <Chip
                        label={card.rarity || 'Unknown'}
                        size="small"
                        className="cr-rarity"
                      />
                      <p className="cr-swipe__link">
                        <a
                          href={card.cardrush_url || `https://www.cardrush-pokemon.jp/product/${card.pid}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          onPointerDown={e => e.stopPropagation()}
                          className="rc-cta"
                        >
                          View on Cardrush ↗
                        </a>
                      </p>
                    </div>

                    <p className="cr-swipe__name">
                      {card.name || 'Unnamed card'}
                    </p>

                    <p className="cr-swipe__price">
                      <span className="cr-swipe__yen">{formatYen(card.price_jpy)}</span>
                      <span className="cr-swipe__gbp">
                        {' '}
                        / {formatGbp(card.price_gbp)}
                      </span>
                    </p>

                    {/* Slab margin calculator */}
                    {(() => {
                      const jpy = card.price_jpy || 0;
                      const costGbp = (jpy * 0.0047) * 1.2;
                      const h = hammerPrice || 40;
                      const whatnotFee = h * 0.06;
                      const processingFee = h * 0.0242 + 0.25;
                      const gradeCost = 8.33;
                      const profitAfterAll = h - costGbp - whatnotFee - processingFee - gradeCost;
                      const isPositive = profitAfterAll > 0;
                      return (
                        <div className="cr-calc">
                          <div className="cr-calc__row">
                            <div className="cr-calc__label">Hammer £</div>
                            <input
                              type="number"
                              value={hammerPrice}
                              onChange={e => setHammerPrice(parseFloat(e.target.value) || 40)}
                              onClick={e => e.stopPropagation()}
                              onMouseDown={e => e.stopPropagation()}
                              className="cr-calc__input"
                            />
                          </div>
                          <div className="cr-calc__figures">
                            <div className="cr-calc__cost">
                              Cost <strong>£{costGbp.toFixed(2)}</strong>
                            </div>
                            <div className="cr-calc__profit" data-tone={isPositive ? 'good' : 'bad'}>
                              Profit after all <strong>£{profitAfterAll.toFixed(2)}</strong>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {!loading && !isComplete ? (
        <div className="cr-vote-bar">
          <Button
            type="button"
            onClick={() => handleVote('no')}
            disabled={!canVote}
            variant="outlined"
            className="cr-vote cr-vote--no"
          >
            ✕
          </Button>
          <Button
            type="button"
            onClick={handleUndo}
            disabled={!canUndo}
            variant="outlined"
            className="cr-vote cr-vote--undo"
          >
            ↩
          </Button>
          <Button
            type="button"
            onClick={() => handleVote('yes')}
            disabled={!canVote}
            variant="outlined"
            className="cr-vote cr-vote--yes"
          >
            ♥
          </Button>
        </div>
      ) : null}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
